<script setup lang="ts">
  import GenericDialog from '@/components/dialog/GenericDialog.vue'
  import { useSpeckleAuthStore } from '@haskoning/vue-speckle'
  import { useSpeckleRedirectUrl } from '@/composables/useSpeckleRedirectUrl'
  import { useSpeckleSsoStore } from '@/stores/speckleSso'

  const props = defineProps({
    modelValue: { type: Boolean, required: true },
    maxWidth: { type: Number, default: 500 }
  })

  const speckleAuthStore = useSpeckleAuthStore()
  const redirectUrl = useSpeckleRedirectUrl()
  const speckleSsoStore = useSpeckleSsoStore()
  const { ssoReauthenticationRequired } = storeToRefs(speckleSsoStore)

  const emit = defineEmits(['update:modelValue'])

  function updateModelValue(value: boolean) {
    emit('update:modelValue', value)
  }

  function onClickLogin() {
    if (ssoReauthenticationRequired.value) {
      redirectUrl.value && speckleAuthStore.promptSsoReauthentication()
      return
    }
    speckleAuthStore.goToSpeckleAuthPage(redirectUrl.value)
  }

  const title = computed(() => {
    if (ssoReauthenticationRequired.value) {
      return 'Speckle SSO Reauthentication Required'
    }
    return 'Speckle Login Required'
  })

  const loginButtonText = computed(() => {
    if (ssoReauthenticationRequired.value) {
      return 'Reauthenticate SSO'
    }
    return 'Continue'
  })

  const text = computed(() => {
    if (ssoReauthenticationRequired.value) {
      return 'Your SSO session has expired. Please reauthenticate to continue using Speckle features.'
    }
    return 'You need to log in to Speckle before performing this action.'
  })
</script>

<template>
  <GenericDialog
    :model-value="props.modelValue"
    :max-width="props.maxWidth"
    @update:model-value="updateModelValue"
    @clicked-outside="updateModelValue(false)"
  >
    <template #title>
      <slot name="title">
        {{ title }}
      </slot>
    </template>
    <template #default>
      <slot>
        <p>{{ text }}</p>
      </slot>
    </template>
    <template #actions>
      <slot name="actions">
        <v-btn variant="text" color="primary" @click="updateModelValue(false)"> Cancel </v-btn>
        <v-btn variant="text" color="primary" @click="onClickLogin"> {{ loginButtonText }} </v-btn>
      </slot>
    </template>
  </GenericDialog>
</template>

<style scoped></style>
